import { useEffect, useState } from 'react';

import { Alert, Box, Link } from '@mui/material';

import StepScreen from '@/karehero-app/access/StepScreen';
import { type Account } from '@/karehero-app/access/AccountBar';
import FactList from '@/karehero-app/tell-us-once/intake/FactList';
import {
  CARE_PROFILE,
  REVIEW_KEPT,
} from '@/karehero-app/tell-us-once/intake/content';

/**
 * What the confirm says when two sources still disagree.
 *
 * Only a clash stops it. Nothing on this list is required: a gap somebody has
 * left is theirs to leave, and so is a figure that has gone stale. A clash is
 * different, because one of the two answers is false and carrying on sends
 * something we already know is wrong.
 *
 * On the screen rather than in the content module, which is being worked on
 * elsewhere. It moves in with the rest of the review's copy once that settles.
 */
const NOT_YET = (n: number) =>
  n === 1
    ? 'One answer has two versions. Pick the right one.'
    : `${n} answers have two versions. Pick the right ones.`;

// Checking what we read, in the flow rather than on the profile.
//
// It was the profile for a while, in an unreviewed state behind an overlay. That
// asked them to review inside a screen they could navigate away from, with a tab
// bar, an app bar and four segments offering everywhere else to be — a lot of
// exits for a screen whose whole job is one answer. So it is a step now: the same
// list, none of the chrome, one way on.
//
// The list is the profile's, though. It is the same facts, and a second copy of
// them would be a second thing to keep true.
//
// It is also where a failure to read what they sent lands. That used to reopen
// the conversation with the error in it, which put them back in a screen they
// had finished and made them find the damage themselves. The reading happens
// after they have gone, so what they need on coming back is what we did get.
const ReviewFactsScreen = ({
  onComplete,
  editing,
  problem = false,
  onTellAgain,
  account,
  kept = false,
}: {
  onComplete?: () => void;
  /** Harness-only: open one fact for editing. See FactList's initialEditing. */
  editing?: string;
  /** Arrived from the ping that says we could not read some of it. */
  problem?: boolean;
  /** Where 'Tell us again' goes. Only offered when something went wrong. */
  onTellAgain?: () => void;
  /** The app bar. See StepScreen's own `account`. */
  account?: Account;
  /**
   * Framed as what we kept rather than what is missing: what their words
   * turned into first, then the gaps. See REVIEW_KEPT.
   */
  kept?: boolean;
}) => {
  // What is still red, and whether they have tried to move on past it. The
  // error waits for the press: a screen that opens already telling somebody off
  // is telling them off for something they have not done yet.
  const [unresolved, setUnresolved] = useState(0);
  // Bumped on each refused press: it shows the message and takes them to the
  // first red row. A count rather than a flag, so pressing again after
  // wandering off brings them back to it.
  const [tried, setTried] = useState(0);
  const [toast, setToast] = useState(false);

  // Up on the press, gone a few seconds later. It says what is wrong; the row
  // it scrolled them to is where the wrong thing is, and that does not go
  // anywhere.
  useEffect(() => {
    if (!tried) return;
    setToast(true);
    const id = setTimeout(() => setToast(false), 4500);
    return () => clearTimeout(id);
  }, [tried]);

  return (
    <StepScreen
      account={account}
      title={kept ? REVIEW_KEPT.title : CARE_PROFILE.review.screenTitle}
      description={
        problem
          ? CARE_PROFILE.review.problem
          : kept
            ? REVIEW_KEPT.body
            : CARE_PROFILE.review.screenBody
      }
      primaryLabel={CARE_PROFILE.review.confirm}
      // Never disabled: a confirm you cannot press says the list is wrong without
      // saying which part, and leaves you pressing it to find out why nothing
      // happens. It presses, and it tells you.
      onPrimary={() => {
        if (unresolved > 0) {
          setTried((n) => n + 1);
          return;
        }
        onComplete?.();
      }}
      overlay={
        // Floating over the step, above the button that refused it. Positioned
        // against the screen root rather than the viewport, so it stays inside
        // the phone: a MUI Snackbar either portals to the document body or
        // measures itself against the window, and both land it on the Loft page
        // around the frame.
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 96,
            px: 3,
            zIndex: 2,
            pointerEvents: 'none',
            opacity: toast && unresolved > 0 ? 1 : 0,
            transform:
              toast && unresolved > 0 ? 'translateY(0)' : 'translateY(8px)',
            transition: 'opacity .2s ease, transform .24s ease',
          }}
        >
          <Alert severity='error' variant='filled'>
            {NOT_YET(unresolved)}
          </Alert>
        </Box>
      }
      footnote={
        problem ? (
          <Link
            component='button'
            type='button'
            variant='body2'
            onClick={() => onTellAgain?.()}
          >
            {CARE_PROFILE.review.tellAgain}
          </Link>
        ) : undefined
      }
    >
      {/* Edit opens the row on its own, with the control its answer takes: a
          nationality is one of a list, a date of birth is a date, and a name is
          three fields. Inline turns every one of those into a text box. */}
      <FactList
        groups={kept ? REVIEW_KEPT.groups : CARE_PROFILE.groups}
        initialEditing={editing}
        edit='drawer'
        onUnresolved={setUnresolved}
        revealUnresolved={tried}
      />
    </StepScreen>
  );
};

export default ReviewFactsScreen;
